import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const SCALE = [
  { v: 0, label: "Not at all" },
  { v: 1, label: "Several days" },
  { v: 2, label: "More than half the days" },
  { v: 3, label: "Nearly every day" },
];

export default function Assessment() {
  const nav = useNavigate();
  const [assess, setAssess] = useState(null);
  const [flat, setFlat] = useState([]);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  useSeo({
    title: "Free Wellness Check-In for Women | Sisterhood+",
    description: "Take a free, no-account wellness check-in from Sisterhood+ to notice how you're really doing. For reflection only — not a diagnosis.",
    path: "/assessment",
  });

  useEffect(() => {
    api.get("/assessment/questions").then(({ data }) => {
      setAssess(data);
      const list = [];
      data.domains.forEach((d) => d.questions.forEach((q) => list.push({ ...q, domain: d.name })));
      setFlat(list);
    });
  }, []);

  if (!assess) return <div className="min-h-screen flex items-center justify-center text-[#291B2D]/60">Loading…</div>;

  const q = flat[i];
  const answered = answers[q?.id] !== undefined;
  const progress = flat.length ? ((i + (answered ? 1 : 0)) / flat.length) * 100 : 0;

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessment/submit", { answers: finalAnswers });
      sessionStorage.setItem("sh_last_result", JSON.stringify(data));
      localStorage.setItem("sh_assessment_seen", "1");
      nav("/assessment/results");
    } catch {
      toast.error("Could not submit. Please try again.");
      setSubmitting(false);
      setAdvancing(false);
    }
  };

  const pick = (v) => {
    if (advancing || submitting) return;
    const next = { ...answers, [q.id]: v };
    setAnswers(next);
    setAdvancing(true);
    setTimeout(() => {
      if (i < flat.length - 1) { setI(i + 1); setAdvancing(false); }
      else submit(next);
    }, 240);
  };

  return (
    <div className="min-h-screen bg-[#FCFAF6] p-6 flex items-center" data-testid="assessment-page">
      <div className="w-full max-w-md lg:max-w-2xl mx-auto">
        <div className="mb-4 flex items-center justify-between text-xs lg:text-sm text-[#291B2D]/50">
          <span>{assess.title}</span>
          <span>{i + 1} of {flat.length}</span>
        </div>
        <div className="h-1 bg-[#291B2D]/10 rounded-full overflow-hidden mb-8">
          <div className="h-full bg-[#C89B55] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="bg-white rounded-3xl p-8 lg:p-12 card-soft animate-fade-up">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-4">{q.domain}</p>
          <h2 className="font-display text-2xl lg:text-3xl leading-snug mb-2">In the past two weeks, how often have you:</h2>
          <p className="text-lg lg:text-xl text-[#291B2D]/80 mb-8 lg:mb-10">{q.text}</p>
          <div className="space-y-2 lg:space-y-3">
            {SCALE.map((s) => (
              <button key={s.v} data-testid={`assessment-answer-${q.id}-${s.v}`} onClick={() => pick(s.v)} disabled={submitting || advancing}
                className={`w-full text-left p-4 lg:p-5 rounded-2xl border transition-smooth text-sm lg:text-base ${answers[q.id] === s.v ? "bg-[#291B2D] text-white border-[#291B2D]" : "bg-[#F7F2EA] border-transparent hover:bg-[#EFE7DA]"}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-between gap-3">
            {i > 0 ? (
              <button data-testid="assessment-back" onClick={() => { if (!advancing && !submitting) setI(i - 1); }} className="text-sm text-[#291B2D]/50 hover:text-[#291B2D] px-2 py-3">← Back</button>
            ) : <span />}
            {submitting
              ? <span className="text-sm text-[#291B2D]/50" data-testid="assessment-scoring">Scoring…</span>
              : <span className="text-xs text-[#291B2D]/40">Tap an answer to continue</span>}
          </div>
        </div>
        <button data-testid="assessment-exit" onClick={() => { localStorage.setItem("sh_assessment_seen", "1"); nav("/login"); }}
          className="block mx-auto text-xs text-[#291B2D]/40 hover:text-[#291B2D] mt-6 underline underline-offset-4">
          Exit to sign in
        </button>
        <p className="text-xs text-[#291B2D]/50 mt-4 text-center">Sisterhood+ is not therapy and does not diagnose.</p>
      </div>
    </div>
  );
}
